import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    TextField,
    MenuItem,
    Typography,
    Grid,
    Paper,
    Divider,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Icon,
    Chip,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    FormHelperText,
    Checkbox,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { DropzoneArea } from "material-ui-dropzone";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import Button from "components/CustomButtons/Button.js";
import { createProjectAPI } from "views/APIs/ProjectsUtility";
import DescriptionIcon from "@material-ui/icons/Description";
import BusinessIcon from "@material-ui/icons/Business";
import NotesIcon from "@material-ui/icons/Notes";
import SaveIcon from "@material-ui/icons/Save";
import { uploadFileCaseStudyAPI, deleteUploadedFileCaseStudyAPI } from "views/APIs/CaseStudyUtility";
import { ContactlessOutlined } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
    root: {},
    paper: {
        padding: theme.spacing(3),
        borderRadius: 16,
        boxShadow: theme.shadows[2],
    },
    fieldCard: {
        padding: theme.spacing(2.25),
        borderRadius: 14,
        boxShadow: theme.shadows[1],
        backgroundColor: theme.palette.background.paper,
        transition: "box-shadow 120ms ease, transform 120ms ease",
        "&:focus-within": {
            boxShadow: theme.shadows[4],
            transform: "translateY(-1px)",
        },
    },
    helper: {
        display: "flex",
        justifyContent: "space-between",
    },
    actionsBar: {
        display: "flex",
        justifyContent: "flex-end",
        marginLeft: "auto",
    },
}));

export async function fetchImageAsFile(imageUrl) {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const file = new File([blob], "qcreate_logo.png", { type: blob.type });
    return file;
}

const CREATE_SENTINEL = "__create_new_project__";
const images = require.context("assets/img/create_cards_16_9/", true);

const ProjectDetails = ({
    caseStudyID,
    caseStudy,
    loading = false,
    associatedProjects = [],
    setActiveStep = () => { },
    onSave = () => { },
    onNext = () => { },
    setFileList = () => { },
    fileList = [],
}) => {
    const classes = useStyles();

    const [caseStudyDetails, setCaseStudyDetails] = useState(caseStudy);
    useEffect(() => {
        setCaseStudyDetails(caseStudy);
        setCaseStudyDetails({ ...caseStudyDetails, has_teaching_note: caseStudyDetails.has_teaching_note || false });
        setCaseStudyDetails({ ...caseStudyDetails, has_lab_details: caseStudyDetails.has_lab_details || false });
    }, [caseStudy]);

    const hasSource = useMemo(() => {
        return !!(caseStudyDetails?.uploaded_files?.length > 0 || caseStudyDetails?.linked_project_id);
    }, [caseStudyDetails?.uploaded_files, caseStudyDetails?.linked_project_id]);

    const [associatedProjectsFromParent, setAssociatedProjectsFromParent] = useState([
        ...associatedProjects,
        { id: CREATE_SENTINEL, name: "+ Create new project" },
    ]);

    useEffect(() => {
        setAssociatedProjectsFromParent([
            ...associatedProjects,
            { id: CREATE_SENTINEL, name: "+ Create new project" },
        ]);
    }, [associatedProjects]);

    const [createOpen, setCreateOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [newDesc, setNewDesc] = useState("");
    const inputId = useMemo(() => `case-study-attachment-${Math.random().toString(36).slice(2)}`, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCaseStudyDetails((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleAssociatedProjectChange = (e) => {
        const val = e.target.value;
        if (val === CREATE_SENTINEL) {
            setCreateOpen(true);
            return;
        }
        setCaseStudyDetails((prev) => ({ ...prev, associated_project_id: val }));
    };

    const [mode, setMode] = useState(
        caseStudyDetails?.linked_project_id ? "existing" : "upload"
    );

    const hasValue = useMemo(
        () =>
            mode === "existing"
                ? !!caseStudyDetails?.linked_project_id
                : !!caseStudyDetails?.file,
        [mode, caseStudyDetails?.linked_project_id, caseStudyDetails?.file]
    );

    const showError = !hasValue;

    const onModeChange = (e) => {
        const next = e.target.value;
        setMode(next);
        setCaseStudyDetails((prev) => ({
            ...prev,
            linked_project_id: next === "existing" ? prev.linked_project_id ?? "" : "",
            file: next === "upload" ? prev.file ?? null : null,
        }));
    };

    const onPlanChange = (e, child) => {
        const value = e?.target?.value ?? child?.props?.value ?? "";
        if (!value) return;
        setCaseStudyDetails((prev) => ({ ...prev, linked_project_id: value, file: null }));
    };

    const createNewProject = async (name, description) => {
        try {
            const formData = new FormData();
            formData.append("project_name", name);
            formData.append("project_description", description);
            const data = await createProjectAPI(formData);
            return data;
        } catch (error) { }
    };

    const handleCreate = async () => {
        if (!newName.trim()) return;
        setCreating(true);
        try {
            const created = await createNewProject(newName.trim(), newDesc.trim());
            const sentinelPopped = associatedProjectsFromParent.pop();
            setAssociatedProjectsFromParent([
                ...associatedProjects,
                { id: created._id, name: created.name },
                sentinelPopped,
            ]);
            setCaseStudyDetails((prev) => ({ ...prev, associated_project_id: created._id }));
            setNewName("");
            setNewDesc("");
            setCreateOpen(false);
        } finally {
            setCreating(false);
        }
    };


    const handleClose = () => {
        setCreateOpen(false);
        setNewName("");
        setNewDesc("");
    };


    const handleRemoveFile = () => {
        setCaseStudyDetails((prev) => ({
            ...prev,
            file: null,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(caseStudyDetails);
    };

    const handleSave = () => {
        onNext(caseStudyDetails);
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            const newFile = files[0];
            const data = await uploadFileCaseStudyAPI(caseStudyID, newFile);
            setFileList([...fileList, { id: data.file_id, name: newFile.name }]);
            setCaseStudyDetails((prev) => ({ ...prev, uploaded_files: [...prev.uploaded_files, { id: data.file_id, name: newFile.name }] }));
        }
        e.target.value = "";
    };

    const handleRemove = async (id) => {
        await deleteUploadedFileCaseStudyAPI(caseStudyID, id); // Remove from backend
        setFileList((prev) => prev.filter((file) => file.id !== id)); // Remove from UI
        setCaseStudyDetails((prev) => ({ ...prev, uploaded_files: prev.uploaded_files.filter((file) => file.id !== id) }));
    };

    useEffect(() => {
    }, [loading, hasSource, caseStudyDetails]);

    return (
        <Box component="form" className={classes.root}>
            <Typography variant="h5">Case Ideation</Typography>
            <Typography variant="body1">
                Define the initial case brief—set the case name, craft a concise overview, and assemble supporting files to seed the workflow.
            </Typography>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant="body2" display="block"><DescriptionIcon fontSize="small" style={{ verticalAlign: "text-bottom", }} /> Case Study Name</Typography>
                    <TextField
                        fullWidth
                        name="name"
                        value={caseStudyDetails.name}
                        onChange={handleChange}
                        margin="normal"
                        required
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="body2" display="block"><BusinessIcon fontSize="small" style={{ verticalAlign: "text-bottom", }} /> Associated Project</Typography>
                    <TextField
                        select
                        fullWidth
                        name="associated_project_id"
                        value={caseStudyDetails.associated_project_id}
                        onChange={handleAssociatedProjectChange}
                        margin="normal"
                        required
                        variant="outlined"
                    >
                        {associatedProjectsFromParent.map((project) => (
                            <MenuItem key={project.id} value={project.id}>
                                {project.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                {/* Create Project Modal */}
                <Dialog open={createOpen} onClose={!creating ? handleClose : undefined} fullWidth maxWidth="sm">
                    <DialogTitle>Create a new project</DialogTitle>
                    <DialogContent dividers>
                        <TextField
                            fullWidth
                            label="Project name"
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            autoFocus
                            margin="dense"
                            required
                            variant="outlined"
                        />
                        <TextField
                            fullWidth
                            label="Description"
                            value={newDesc}
                            onChange={(e) => setNewDesc(e.target.value)}
                            margin="dense"
                            multiline
                            minRows={3}
                            variant="outlined"
                        />
                        <Typography variant="caption" color="textSecondary">
                            A new project will be created and selected as the Associated Project.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} disabled={creating}>
                            Cancel
                        </Button>
                        <Button
                            color="quCreateColor"
                            variant="contained"
                            onClick={handleCreate}
                            disabled={creating || !newName.trim()}
                        >
                            {creating ? "Creating…" : "Create"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Grid item xs={12}>
                    <Typography variant="body2" display="block"><NotesIcon fontSize="small" style={{ verticalAlign: "text-bottom", }} /> Case Study Description</Typography>
                    <TextField
                        fullWidth
                        name="description"
                        value={caseStudyDetails.description}
                        onChange={handleChange}
                        margin="normal"
                        multiline
                        rows={4}
                        variant="outlined"
                    />
                </Grid>

                <Grid item xs={12}>
                    <FormControl component="fieldset" error={showError} fullWidth>


                        {/* EXISTING PLAN MODE */}
                        {mode === "existing" && (
                            <Box mt={2}>
                                <Typography variant="caption">Pick a project to fetch and convert into a case study.</Typography>
                                <Select
                                    fullWidth
                                    name="linked_project_id"
                                    value={caseStudyDetails?.linked_project_id ?? ""}
                                    onChange={onPlanChange}
                                    displayEmpty
                                    renderValue={(val) =>
                                        val ? associatedProjects.find((p) => p._id === val)?.name ?? val : "Select a project…"
                                    }
                                    variant="outlined"
                                >
                                    <MenuItem disabled value="">
                                        <em>Select a project…</em>
                                    </MenuItem>
                                    {associatedProjects.map((p) => (
                                        <MenuItem key={p._id} value={p._id}>
                                            {p.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {showError && <FormHelperText>Please select a plan.</FormHelperText>}
                            </Box>
                        )}


                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={caseStudyDetails.has_teaching_note}
                                        onChange={(e) => setCaseStudyDetails({ ...caseStudyDetails, has_teaching_note: e.target.checked })}
                                        name="has_teaching_note"
                                        color="#20677b"
                                    />
                                }
                                style={{ color: '#000' }}
                                label="Generate Teaching Note"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={caseStudyDetails.has_lab_details}
                                        onChange={(e) => setCaseStudyDetails({ ...caseStudyDetails, has_lab_details: e.target.checked })}
                                        name="has_lab_details"
                                        color="#20677b"
                                    />
                                }
                                style={{ color: '#000' }}
                                label="Lab"
                            />
                        </Grid>


                        {/* UPLOAD MODE */}
                        {mode === "upload" && (
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Typography variant="body1" style={{ marginRight: 8 }}>
                                        Attach files:
                                    </Typography>
                                    <input
                                        id={inputId}
                                        type="file"
                                        accept=".pdf,application/pdf"
                                        style={{ display: "none" }}
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor={inputId}>
                                        <IconButton
                                            component="span"
                                            size="small"
                                            aria-label="Attach file"
                                        >
                                            <AttachFileIcon />
                                            <span style={{ marginLeft: 4, fontWeight: "bold", fontSize: 18 }}>+</span>
                                        </IconButton>
                                    </label>
                                </Box>
                                {/* Attached files as chips */}
                                {fileList.length > 0 && (
                                    <Box mt={1} display="flex" flexWrap="wrap">
                                        {fileList.map((file) => {
                                            return (
                                                <Box mr={1} mb={1}>
                                                    <Chip
                                                        label={file.name} // <-- Only show file name
                                                        onDelete={() => handleRemove(file.id)}
                                                        deleteIcon={<CloseIcon fontSize="small" />}
                                                        variant="default"
                                                    />
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Grid>
                        )}
                    </FormControl>
                </Grid>
            </Grid>

            <div className={classes.actionsBar}>
                <Box style={{ marginRight: 10, marginTop: 10 }}>

                    <Button
                        color="quCreateColor"
                        onClick={handleSubmit}
                        disabled={
                            loading || !hasSource || !caseStudyDetails.name || !caseStudyDetails.associated_project_id
                        }
                        style={{ marginRight: 10 }}
                    >
                        <SaveIcon />
                        {loading ? <CircularProgress size={24} /> : "Save Changes"}
                    </Button>
                    <Button
                        color="quCreateColor"
                        variant="contained"
                        onClick={handleSave}
                        disabled={
                            loading || !hasSource || !caseStudyDetails.name || !caseStudyDetails.associated_project_id
                        }
                    >
                        Next
                    </Button>
                </Box>
            </div>
        </Box>
    );
};

export default ProjectDetails;
